import React, { useMemo, useState } from "react";
import { countLevels, Dash, Empty, FilterChips, fmt, fmt2, hrs, levelOf, MARKETPLACES, Marketplace, Num, Reasons, RebuildChip, Tag } from "./ui";

const FILTERS = [["all", "All"], ["red", "Needs dev work"], ["amber", "Recovering"], ["ok", "Healthy"]];
const COLUMNS = [
  ["store_name", "Store"], ["severity", "Needs action"], ["received_traffic", "Records today", true],
  ["pending", "Waiting for rebuild", true], ["orders_gap", "Orders gap", true],
  ["hours_since_success", "Rebuild"], ["dw_hourly_waiting", "Waiting for warehouse", true], [null, "Alert"],
];

function compare(a, b, key, dir) {
  let x = a[key], y = b[key];
  if (typeof x === "string") x = x.toLowerCase();
  if (typeof y === "string") y = y.toLowerCase();
  if (x == null && y == null) return 0;
  if (x == null) return 1;
  if (y == null) return -1;
  return ((x > y ? 1 : x < y ? -1 : 0) * dir) || Math.abs(b.orders_gap) - Math.abs(a.orders_gap);
}

export default function StoresTab({ stores, filter, onFilter }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "severity", dir: -1 });

  const counts = useMemo(() => countLevels(stores, (r) => levelOf(r.severity)), [stores]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return stores
      .filter((r) => filter === "all" || levelOf(r.severity) === filter)
      .filter((r) => !needle || [r.store_name, r.profile_id, r.marketplace_id, MARKETPLACES[r.marketplace_id], r.seller_partner_id]
        .join(" ").toLowerCase().includes(needle))
      .sort((a, b) => compare(a, b, sort.key, sort.dir));
  }, [stores, filter, q, sort]);

  const sortBy = (key) => key && setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));

  return (
    <>
      <div className="ams-toolbar">
        <FilterChips options={FILTERS} value={filter} onChange={onFilter} counts={counts} />
        <input className="ams-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search store, profile or marketplace" aria-label="Search stores" />
      </div>
      <p className="ams-hint">Red rows need the dev team. Orange rows should recover on their own. Click a column to sort.</p>
      <div className="ams-tablewrap">
        <table className="ams-table">
          <thead>
            <tr>
              {COLUMNS.map(([key, label, num], i) => {
                const active = key && sort.key === key && !(key === "severity" && i !== 1);
                return (
                  <th key={label} className={`${num ? "ams-num" : ""}${key ? " ams-sortable" : ""}`}
                    aria-sort={active ? (sort.dir > 0 ? "ascending" : "descending") : undefined}
                    tabIndex={key ? 0 : undefined} onClick={() => sortBy(key)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), sortBy(key))}>
                    {label}{active ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((r) => {
              const lvl = levelOf(r.severity);
              return (
                <tr key={r.row_id} className={`ams-edge--${lvl}`}>
                  <td className="ams-name">
                    {r.store_name || <span className="ams-dash">Unknown store</span>}
                    <dl className="ams-idlist">
                      <dt>Seller</dt><dd>{r.seller_partner_id || <Dash />}</dd>
                      <dt>Market</dt><dd><Marketplace id={r.marketplace_id} /></dd>
                      <dt>Profile</dt><dd>{r.profile_id || <span className="ams-why--red">not linked</span>}</dd>
                    </dl>
                  </td>
                  <td className="ams-why"><Reasons issues={r.issues} /></td>
                  <td className="ams-num">
                    <Num value={r.received_traffic} /> <span className="ams-dash">traffic</span>
                    <div><Num value={r.received_conversion} /> <span className="ams-dash">conversion</span></div>
                  </td>
                  <td className={`ams-num${r.pending ? " ams-warn" : ""}`}><Num value={r.pending} /></td>
                  <td className={`ams-num${Math.abs(r.orders_gap) > 0.001 ? " ams-warn" : ""}`}>{fmt2(r.orders_gap)}</td>
                  <td>
                    <RebuildChip status={r.status} />
                    <div className="ams-sub">{r.hours_since_success == null ? "never succeeded" : `${hrs(r.hours_since_success)} ago`}</div>
                  </td>
                  <td className="ams-num">
                    {fmt(r.dw_hourly_waiting)}
                    {r.dw_hourly_waiting > 0 && <div className="ams-sub">oldest {hrs(r.dw_hourly_oldest_h)}</div>}
                  </td>
                  <td><Tag level={lvl} /></td>
                </tr>
              );
            }) : (
              <Empty cols={COLUMNS.length}>
                {filter !== "all" || q ? "No stores match this filter." : "No stores yet. Run a full rebuild to register them."}
              </Empty>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
